import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { PrivateNote, ShareableEntity } from '../types/privateNote';
import { X, User, Users, Bot, Plus, Edit2, Save } from 'lucide-react';
import { cn } from '../lib/utils';

interface NoteDetailModalProps {
  note: PrivateNote | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (note: PrivateNote) => void;
  availablePeople: ShareableEntity[];
  availableGroups: ShareableEntity[];
  availableBots: ShareableEntity[];
}

export const NoteDetailModal: React.FC<NoteDetailModalProps> = ({
  note,
  isOpen,
  onClose,
  onSave,
  availablePeople,
  availableGroups,
  availableBots,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [editedContent, setEditedContent] = useState('');
  const [sharedWith, setSharedWith] = useState<PrivateNote['sharedWith']>({
    people: [],
    groups: [],
    bots: [],
  });
  const [showAddPeople, setShowAddPeople] = useState(false);
  const [showAddGroups, setShowAddGroups] = useState(false);
  const [showAddBots, setShowAddBots] = useState(false);

  // Initialize form when note changes
  React.useEffect(() => {
    if (note) {
      setEditedTitle(note.title);
      setEditedContent(note.content);
      setSharedWith(note.sharedWith);
      setIsEditing(false);
    }
  }, [note]);

  const handleSave = () => {
    if (!note) return;

    const updatedNote: PrivateNote = {
      ...note,
      title: editedTitle,
      content: editedContent,
      updatedAt: new Date(),
      sharedWith,
    };

    onSave(updatedNote);
    setIsEditing(false);
  };

  const handleAddShare = (entity: ShareableEntity) => {
    const key = entity.type === 'person' ? 'people' : entity.type === 'group' ? 'groups' : 'bots';
    if (!sharedWith[key].includes(entity.id)) {
      setSharedWith({
        ...sharedWith,
        [key]: [...sharedWith[key], entity.id],
      });
    }
  };

  const handleRemoveShare = (type: 'people' | 'groups' | 'bots', id: string) => {
    setSharedWith({
      ...sharedWith,
      [type]: sharedWith[type].filter(item => item !== id),
    });
  };

  const getEntityName = (type: 'people' | 'groups' | 'bots', id: string): string => {
    const entities = type === 'people' ? availablePeople : type === 'groups' ? availableGroups : availableBots;
    return entities.find(e => e.id === id)?.name || id;
  };

  const getAvailableEntities = (type: 'people' | 'groups' | 'bots'): ShareableEntity[] => {
    const entities = type === 'people' ? availablePeople : type === 'groups' ? availableGroups : availableBots;
    return entities.filter(e => !sharedWith[type].includes(e.id));
  };

  if (!note) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pr-10">
          <div className="flex items-center justify-between">
            <DialogTitle>
              {isEditing ? 'Edit Memory' : 'Memory Details'}
            </DialogTitle>
            <div className="flex gap-2">
              {!isEditing ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                >
                  <Edit2 className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSave}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </Button>
              )}
            </div>
          </div>
          <DialogDescription>
            {note.author} • Last updated {note.updatedAt.toLocaleString()}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Title */}
          <div>
            <label className="text-sm font-medium mb-2 block">Title</label>
            {isEditing ? (
              <Input
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                placeholder="Note title"
              />
            ) : (
              <p className="text-sm font-semibold">{note.title}</p>
            )}
          </div>

          {/* Content */}
          <div>
            <label className="text-sm font-medium mb-2 block">Content</label>
            {isEditing ? (
              <textarea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                placeholder="Note content"
                className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
            ) : (
              <p className="text-sm whitespace-pre-wrap">{note.content}</p>
            )}
          </div>

          <Separator />

          {/* Access Control */}
          <div>
            <h3 className="text-sm font-semibold mb-3">Access Control</h3>

            {/* Shared with People */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">People</span>
                </div>
                {isEditing && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAddPeople(!showAddPeople)}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add
                  </Button>
                )}
              </div>
              {showAddPeople && isEditing && (
                <div className="mb-2 p-2 border rounded-md bg-muted/50">
                  {getAvailableEntities('people').length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {getAvailableEntities('people').map((person) => (
                        <Button
                          key={person.id}
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            handleAddShare(person);
                            setShowAddPeople(false);
                          }}
                          className="h-7 text-xs"
                        >
                          <User className="h-3 w-3 mr-1" />
                          {person.name}
                        </Button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">All available people are already added</p>
                  )}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {sharedWith.people.length > 0 ? (
                  sharedWith.people.map((personId) => (
                    <Badge
                      key={personId}
                      variant="secondary"
                      className="flex items-center gap-1"
                    >
                      <User className="h-3 w-3" />
                      {getEntityName('people', personId)}
                      {isEditing && (
                        <button
                          onClick={() => handleRemoveShare('people', personId)}
                          className="ml-1 hover:bg-destructive/20 rounded-full p-0.5"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </Badge>
                  ))
                ) : (
                  <span className="text-xs text-muted-foreground">Not shared with anyone</span>
                )}
              </div>
            </div>

            {/* Shared with Groups */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Groups</span>
                </div>
                {isEditing && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAddGroups(!showAddGroups)}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add
                  </Button>
                )}
              </div>
              {showAddGroups && isEditing && (
                <div className="mb-2 p-2 border rounded-md bg-muted/50">
                  {getAvailableEntities('groups').length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {getAvailableEntities('groups').map((group) => (
                        <Button
                          key={group.id}
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            handleAddShare(group);
                            setShowAddGroups(false);
                          }}
                          className="h-7 text-xs"
                        >
                          <Users className="h-3 w-3 mr-1" />
                          {group.name}
                        </Button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">All available groups are already added</p>
                  )}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {sharedWith.groups.length > 0 ? (
                  sharedWith.groups.map((groupId) => (
                    <Badge
                      key={groupId}
                      variant="secondary"
                      className="flex items-center gap-1"
                    >
                      <Users className="h-3 w-3" />
                      {getEntityName('groups', groupId)}
                      {isEditing && (
                        <button
                          onClick={() => handleRemoveShare('groups', groupId)}
                          className="ml-1 hover:bg-destructive/20 rounded-full p-0.5"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </Badge>
                  ))
                ) : (
                  <span className="text-xs text-muted-foreground">Not shared with any groups</span>
                )}
              </div>
            </div>

            {/* Shared with Bots */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Bot className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Bots</span>
                </div>
                {isEditing && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAddBots(!showAddBots)}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add
                  </Button>
                )}
              </div>
              {showAddBots && isEditing && (
                <div className="mb-2 p-2 border rounded-md bg-muted/50">
                  {getAvailableEntities('bots').length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {getAvailableEntities('bots').map((assistant) => (
                        <Button
                          key={assistant.id}
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            handleAddShare(assistant);
                            setShowAddBots(false);
                          }}
                          className="h-7 text-xs"
                        >
                          <Bot className="h-3 w-3 mr-1" />
                          {assistant.name}
                        </Button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">All available bots are already added</p>
                  )}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {sharedWith.bots.length > 0 ? (
                  sharedWith.bots.map((botId) => (
                    <Badge
                      key={botId}
                      variant="secondary"
                      className="flex items-center gap-1"
                    >
                      <Bot className="h-3 w-3" />
                      {getEntityName('bots', botId)}
                      {isEditing && (
                        <button
                          onClick={() => handleRemoveShare('bots', botId)}
                          className="ml-1 hover:bg-destructive/20 rounded-full p-0.5"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </Badge>
                  ))
                ) : (
                  <span className="text-xs text-muted-foreground">Not shared with any bots</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

